'use es6';

import { NavMarker } from 'react-rhumb';
import Raven from 'raven-js';
import { Component, Suspense, lazy } from 'react';
import { LocaleContext } from '../components/ui/misc/LocaleProvider';
import { ProductTypes, UnsubscribeStatuses } from '../Constants';
import { parseQueryString } from '../utils/qs';
import { createPreferencesService } from '../services/preferences';
import { getAlert, parseApiError, sanitizeEmail } from '../utils';
import Flex from '../components/ui/layout/Flex';
import LoadingSpinner from '../components/ui/misc/LoadingSpinner';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SubscriptionPreferences = /*#__PURE__*/lazy(() => import( /* webpackChunkName: "subscription-preferences" */'../components/SubscriptionPreferences'));
const Alert = /*#__PURE__*/lazy(() => import( /* webpackChunkName: "alert" */'../components/ui/misc/Alert'));
const InvalidProduct = /*#__PURE__*/lazy(() => import( /* webpackChunkName: "error-states" */'../components/InvalidProduct'));
const UserError = /*#__PURE__*/lazy(() => import( /* webpackChunkName: "error-states" */'../components/UserError'));
const RedirectWrapper = /*#__PURE__*/lazy(() => import( /* webpackChunkName: "error-states" */'../components/RedirectWrapper'));
class SubscriptionPreferencesContainer extends Component {
  constructor(props) {
    super(props);
    this.handleChangeLanguage = languageCode => {
      const {
        setLocale
      } = this.context;
      if (setLocale) {
        // setLocale will always be available here but
        setLocale(languageCode);
      }
      return this.fetchPreferences({
        languagePreference: languageCode
      });
    };
    this.handleEmailChange = email => {
      const oldEmail = this.state.email;
      this.setState({
        email
      });
      if (email !== oldEmail) {
        this.fetchPreferences({
          email
        }, false).then(() => {
          this.setState({
            hasValidEmail: true,
            emailValidationError: false
          });
        }).catch(() => {
          this.setState({
            emailValidationError: true
          });
        });
      }
    };
    const {
      email: _email,
      product,
      d: key,
      v: version,
      s,
      checkSubscriptions,
      // If isTest and groupId are passed, this is a link
      // for previewing the unsubscribe page from settings.
      isTest,
      groupId: testGroupId
    } = parseQueryString(props.location.search);
    const isSalesProductNoEmail = product === 'sales' && !_email;
    const isOneToOneEmail = Boolean(version === '5');
    const isLegacyLink = Boolean((isSalesProductNoEmail || _email) && key && product);
    const isPreferencesGroupLink = Boolean(version === '4' && !!key);
    const hasEmptyQueryString = props.location.search === '';
    const hasMissingOrInvalidQueryParams = !isSalesProductNoEmail && !isLegacyLink && !isPreferencesGroupLink && !isOneToOneEmail && !hasEmptyQueryString;
    this.preferencesService = createPreferencesService({
      isOneToOneEmail,
      isLegacyLink,
      isTest,
      testGroupId
    });
    this.preferencesService.checkRedirect({
      key,
      version,
      product,
      email: _email,
      s
    }).then(({
      url,
      shouldRedirect
    }) => {
      if (shouldRedirect) {
        this.setState({
          redirectUrl: url,
          status: UnsubscribeStatuses.REDIRECT
        });
        return;
      }
    }).catch(() => {
      this.redirectToFailure();
    });
    let company = {
      logoUrl: null,
      name: null
    };
    let subscriptionData = {
      brandId: null,
      email: _email ? decodeURIComponent(_email) : '',
      hasEmail: !!_email,
      optedInIds: [],
      originalIds: [],
      optOutAllSubscriptions: Boolean(checkSubscriptions || !isLegacyLink && !isPreferencesGroupLink),
      types: []
    };
    let status = UnsubscribeStatuses.NOT_STARTED;
    if (window.emailUnsubscribeData !== undefined && window.emailUnsubscribeData.company !== undefined && window.emailUnsubscribeData.subscriptionData !== undefined) {
      company = window.emailUnsubscribeData.company;
      subscriptionData = Object.assign({}, subscriptionData, window.emailUnsubscribeData.subscriptionData);
      subscriptionData.email = sanitizeEmail(subscriptionData.email);
      status = UnsubscribeStatuses.SUCCESS;
    }
    this.state = Object.assign({
      alerts: [],
      emailValidationError: false,
      hasValidEmail: !isSalesProductNoEmail,
      hasEmptyQueryString,
      hasMissingOrInvalidQueryParams,
      isPreferencesGroupLink,
      isLegacyLink,
      isSalesProductNoEmail,
      isOneToOneEmail,
      key,
      version,
      s,
      product: product ? decodeURIComponent(product) : '',
      status,
      company,
      redirectUrl: ''
    }, subscriptionData);
    this.handleFailure = this.handleFailure.bind(this);
    this.handleSuccess = this.handleSuccess.bind(this);
    this.unsubscribeFromAll = this.unsubscribeFromAll.bind(this);
    this.updateSubscriptions = this.updateSubscriptions.bind(this);
    this.handleSelectToggle = this.handleSelectToggle.bind(this);
    this.toggleAllSelection = this.toggleAllSelection.bind(this);
  }
  componentDidMount() {
    const {
      hasEmptyQueryString,
      hasMissingOrInvalidQueryParams,
      isPreferencesGroupLink,
      isLegacyLink,
      isSalesProductNoEmail,
      isOneToOneEmail
    } = this.state;
    if (isSalesProductNoEmail) {
      // User needs to enter an email to continue
      return null;
    }

    // query params check
    if (hasEmptyQueryString || hasMissingOrInvalidQueryParams) {
      return null;
    }

    // unknown error
    if (!isPreferencesGroupLink && !isLegacyLink && !isOneToOneEmail) {
      return Raven.captureMessage('Unknown error', {
        level: 'error',
        extra: {
          url: this.props.location,
          search: this.props.location.search,
          referrer: document.referrer
        }
      });
    }
    this.spinnerTimeout = setTimeout(() => {
      if (this.state.status === UnsubscribeStatuses.NOT_STARTED) {
        this.setState({
          status: UnsubscribeStatuses.PENDING
        });
      }
    }, 300);
    return this.fetchPreferences();
  }

  // Use built-in redirection to ensure react-rhumb abandons the current route (else affects SLA scores) https://hubspot.slack.com/archives/C4ABAB8CR/p1682343345825429
  redirectToFailure() {
    const {
      history
    } = this.props;
    history.push('/loadFailure');
  }
  fetchPreferences({
    version = this.state.version,
    key = this.state.key,
    product = this.state.product,
    email = this.state.email,
    s = this.state.s,
    languagePreference = this.context.selectedLocale || null
  } = {}, shouldCatch = true) {
    const promise = this.preferencesService.fetchPreferences({
      version,
      key,
      product,
      email,
      s,
      languagePreference
    }).then(response => {
      clearTimeout(this.spinnerTimeout);
      if (this.state.status !== UnsubscribeStatuses.REDIRECT) {
        this.setState(Object.assign({}, response, {
          status: UnsubscribeStatuses.SUCCESS
        }));
      }
    });
    return shouldCatch ? promise.catch(this.handleFailure) : promise;
  }
  handleFailure(error, additionalState = {}) {
    let status;
    const {
      product,
      email
    } = this.state;
    const {
      isInvalidProduct,
      isWrongEmail,
      isUserError
    } = parseApiError(error);
    if (isInvalidProduct) {
      status = UnsubscribeStatuses.INVALID_PRODUCT;
    } else if (product === ProductTypes.SALES && isWrongEmail) {
      status = UnsubscribeStatuses.WRONG_EMAIL;
    } else if (isUserError) {
      status = UnsubscribeStatuses.USER_ERROR;
    } else {
      this.redirectToFailure();
    }
    this.setState(Object.assign({
      status,
      alerts: getAlert(status, {
        email: sanitizeEmail(email)
      })
    }, additionalState));
  }
  handleSuccess() {
    const {
      email
    } = this.state;
    const status = UnsubscribeStatuses.SUCCESS;
    this.setState({
      status,
      alerts: getAlert(status, {
        email: sanitizeEmail(email)
      })
    });
  }
  unsubscribeFromAll() {
    const {
      email,
      key,
      s,
      product,
      originalIds,
      brandId,
      version
    } = this.state;
    this.setState({
      alerts: [],
      status: UnsubscribeStatuses.PENDING
    });
    this.preferencesService.unsubscribeFromAll({
      version,
      email,
      product: product || ProductTypes.STARTER,
      key,
      s,
      brandId,
      originalIds
    }).then(() => {
      this.toggleAllSelection(true);
      this.handleSuccess(true);
    }).catch(this.handleFailure);
  }
  updateSubscriptions() {
    const {
      key,
      brandId,
      originalIds,
      optedInIds,
      product,
      email,
      version,
      s
    } = this.state;
    this.setState({
      alerts: [],
      status: UnsubscribeStatuses.PENDING
    });
    this.preferencesService.updateSubscriptions({
      version,
      brandId,
      email,
      s,
      key,
      optedInIds,
      originalIds,
      product
    }).then(() => {
      this.handleSuccess(false);
    }).catch(this.handleFailure);
  }
  handleSelectToggle(typeId, isChecked) {
    this.setState(({
      optedInIds: oldOptedInIds
    }) => ({
      optedInIds: isChecked ? [...oldOptedInIds, typeId] : oldOptedInIds.filter(id => id !== typeId)
    }));
  }
  toggleAllSelection(clear = false) {
    this.setState(({
      types: prevTypes,
      optedInIds: prevOptedInIds
    }) => ({
      optedInIds: prevOptedInIds.size > 0 || clear ? [] : prevTypes.map(type => type.id)
    }));
  }
  render() {
    const {
      alerts,
      hasEmptyQueryString,
      hasMissingOrInvalidQueryParams,
      status
    } = this.state;
    const {
      selectedLocale
    } = this.context;
    if (status === UnsubscribeStatuses.REDIRECT) {
      return /*#__PURE__*/_jsx(NavMarker, {
        name: "SUBSCRIPTIONS_REDIRECT",
        children: /*#__PURE__*/_jsx(Suspense, {
          fallback: /*#__PURE__*/_jsx(LoadingSpinner, {
            className: "m-top-15",
            grow: true
          }),
          children: /*#__PURE__*/_jsx(RedirectWrapper, {
            url: this.state.redirectUrl
          })
        })
      });
    }
    if (status === UnsubscribeStatuses.WRONG_EMAIL) {
      return /*#__PURE__*/_jsx(NavMarker, {
        name: "SUBSCRIPTIONS_INVALID_QUERY_PARAMS"
      });
    }
    return /*#__PURE__*/_jsxs("div", {
      className: "p-y-15",
      "data-test-id": "subscription-preferences-container",
      children: [hasEmptyQueryString && /*#__PURE__*/_jsx(NavMarker, {
        name: "SUBSCRIPTIONS_EMPTY_QUERY_PARAMS"
      }), hasMissingOrInvalidQueryParams && /*#__PURE__*/_jsx(NavMarker, {
        name: "SUBSCRIPTIONS_INVALID_QUERY_PARAMS"
      }), /*#__PURE__*/_jsxs(Flex, {
        align: "stretch",
        className: "m-x-auto m-bottom-8",
        direction: "column",
        justify: "center",
        style: {
          maxWidth: 680
        },
        children: [status === UnsubscribeStatuses.PENDING &&
        /*#__PURE__*/
        // NavMarker Placeholder update for debugging latency issues
        _jsx(NavMarker, {
          name: "SUBSCRIPTIONS_PRE_LOAD",
          children: /*#__PURE__*/_jsx(LoadingSpinner, {
            className: "m-top-15",
            grow: true
          })
        }), status === UnsubscribeStatuses.INVALID_PRODUCT && /*#__PURE__*/_jsx(NavMarker, {
          name: "SUBSCRIPTIONS_INVALID_PRODUCT",
          children: /*#__PURE__*/_jsx(Suspense, {
            fallback: /*#__PURE__*/_jsx(LoadingSpinner, {
              className: "m-top-15",
              grow: true
            }),
            children: /*#__PURE__*/_jsx(InvalidProduct, {
              email: this.state.email,
              isUnsubscribeAll: this.state.optOutAllSubscriptions
            })
          })
        }), status === UnsubscribeStatuses.USER_ERROR && /*#__PURE__*/_jsx(NavMarker, {
          name: "SUBSCRIPTIONS_USER_ERROR",
          children: /*#__PURE__*/_jsx(Suspense, {
            fallback: /*#__PURE__*/_jsx(LoadingSpinner, {
              className: "m-top-15",
              grow: true
            }),
            children: /*#__PURE__*/_jsx(UserError, {})
          })
        }), (status === UnsubscribeStatuses.SUCCESS || !this.state.hasValidEmail) && /*#__PURE__*/_jsxs(NavMarker, {
          name: "SUBSCRIPTIONS_LOADED",
          children: [alerts.length > 0 && /*#__PURE__*/_jsx(Suspense, {
            fallback: null,
            children: alerts.map(alert => /*#__PURE__*/_jsx(Alert, {
              "data-test-id": `${alert.id}-alert`.toLowerCase(),
              timestamp: alert.timestamp,
              titleText: alert.titleText,
              type: alert.type,
              children: alert.message
            }, alert.id))
          }), /*#__PURE__*/_jsx(Suspense, {
            fallback: /*#__PURE__*/_jsx(LoadingSpinner, {
              className: "m-top-15",
              grow: true
            }),
            children: /*#__PURE__*/_jsx(SubscriptionPreferences, {
              company: this.state.company,
              email: this.state.email,
              emailValidationError: this.state.emailValidationError,
              hasEmail: this.state.hasEmail,
              hasValidEmail: this.state.hasValidEmail,
              isUnsubscribeAll: this.state.optOutAllSubscriptions,
              language: selectedLocale,
              onChangeLanguage: this.handleChangeLanguage,
              onEmailChange: this.handleEmailChange,
              onSavePreferences: this.updateSubscriptions,
              onSelectToggle: this.handleSelectToggle,
              onUnsubscribeFromAll: this.unsubscribeFromAll,
              optedInIds: this.state.optedInIds,
              subscriptionTypes: this.state.types,
              validLanguages: this.state.validLanguages
            })
          })]
        })]
      })]
    });
  }
}
SubscriptionPreferencesContainer.preferencesService = null;
SubscriptionPreferencesContainer.spinnerTimeout = null;
SubscriptionPreferencesContainer.contextType = LocaleContext;
export default SubscriptionPreferencesContainer;